import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getTillId } from '../utils/tillId';
import { useStore } from '../context/StoreContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Send, Trash2, ImagePlus, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function StoreChat() {
  const { token, user, activeStaff } = useAuth();
  const { selectedStore, storeName, stores } = useStore();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);

  const getAuthHeader = () => ({ Authorization: `Bearer ${token}` });
  const senderName = activeStaff?.name || user?.name || 'Unknown';
  const myTillId = getTillId();
  const isMine = (m) => (m.till_id ? m.till_id === myTillId : m.sender_name === senderName);
  const prevMessageCount = useRef(0);

  const playDing = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 830;
      osc.type = 'sine';
      gain.gain.value = 0.3;
      osc.start();
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.stop(ctx.currentTime + 0.3);
    } catch (e) {}
  };

  const newestRef = useRef(null);

  // First load pulls the recent history; every poll after that asks only for messages newer than the last one seen
  const fetchMessages = async () => {
    try {
      const after = newestRef.current;
      const response = await axios.get(`${API_URL}/chat/messages`, { params: after ? { after, limit: 200 } : { limit: 200 }, headers: getAuthHeader() });
      const incoming = response.data || [];
      if (after && incoming.length === 0) return;
      const merged = after ? [...messages, ...incoming.filter(m => !messages.some(x => x.id === m.id))] : incoming;
      const latest = incoming[incoming.length - 1];
      if (latest) newestRef.current = latest.created_at;
      if (after && latest && !isMine(latest)) playDing();
      prevMessageCount.current = merged.length;
      setMessages(merged);
      localStorage.setItem('chat_last_seen', new Date().toISOString());
    } catch (error) {
      // silent
    }
  };

  const fetchRef = useRef(fetchMessages);
  fetchRef.current = fetchMessages;

  useEffect(() => {
    fetchRef.current();
    // Poll every 5 seconds — only new messages travel over the wire
    pollRef.current = setInterval(() => fetchRef.current(), 5000);
    return () => clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && !imageFile) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('text', newMessage.trim());
      formData.append('store', selectedStore);
      formData.append('sender_name', senderName);
      formData.append('till_id', myTillId);
      if (imageFile) {
        formData.append('image', imageFile);
      }
      await axios.post(`${API_URL}/chat/messages`, formData, {
        headers: getAuthHeader()
      });
      setNewMessage('');
      setImageFile(null);
      setImagePreview(null);
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/chat/messages/${id}`, { headers: getAuthHeader() });
      setMessages(prev => prev.filter(m => m.id !== id));
    } catch (error) {
      toast.error('Cannot delete this message');
    }
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();
    
    const time = d.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit' });
    if (isToday) return time;
    if (isYesterday) return `Yesterday ${time}`;
    return d.toLocaleDateString('en-NZ', { day: '2-digit', month: '2-digit' }) + ' ' + time;
  };

  const getStoreColor = (store) => {
    return store === 'orewa' ? 'bg-blue-500' : 'bg-purple-500';
  };

  const getStoreLabel = (store) => {
    return storeName(store);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-80px)] max-w-3xl mx-auto" data-testid="store-chat-page">
      {/* Header */}
      <div className="px-4 py-3 border-b bg-white">
        <h1 className="text-xl font-bold text-slate-900">Store Chat</h1>
        <p className="text-sm text-slate-500">{stores.length > 1 ? `Messages between ${stores.map(s => s.name).join(' & ')}` : 'Messages between your tills'}</p>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-slate-50">
        {messages.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <p className="text-lg">No messages yet</p>
            <p className="text-sm mt-1">Start a conversation between stores</p>
          </div>
        )}
        {messages.map((msg, idx) => {
          const isMe = isMine(msg);
          const showDate = idx === 0 || 
            new Date(msg.created_at).toDateString() !== new Date(messages[idx-1].created_at).toDateString();
          
          return (
            <React.Fragment key={msg.id}>
              {showDate && (
                <div className="text-center text-xs text-slate-400 py-2">
                  {new Date(msg.created_at).toLocaleDateString('en-NZ', { weekday: 'long', day: 'numeric', month: 'short' })}
                </div>
              )}
              <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`} data-testid={`chat-msg-${msg.id}`}>
                <div className={`max-w-[75%] ${isMe ? 'order-2' : ''}`}>
                  <div className={`rounded-lg px-3 py-2 ${
                    isMe ? 'bg-emerald-100 text-emerald-900' : 'bg-white border border-slate-200 text-slate-900'
                  }`}>
                    {msg.image && (
                      <img src={msg.image} alt="" className="rounded-md max-w-full max-h-64 mb-1 cursor-pointer" onClick={() => window.open(msg.image, '_blank')} />
                    )}
                    {msg.text && <p className="text-sm font-medium whitespace-pre-wrap">{msg.text}</p>}
                  </div>
                  <div className={`flex items-center gap-2 mt-0.5 ${isMe ? 'justify-end' : ''}`}>
                    <span className={`inline-block w-2 h-2 rounded-full ${getStoreColor(msg.store)}`}></span>
                    <span className="text-xs text-slate-400">
                      {msg.sender_name} · {getStoreLabel(msg.store)} · {formatTime(msg.created_at)}
                    </span>
                    {isMe && (
                      <button onClick={() => handleDelete(msg.id)} className="text-slate-300 hover:text-red-500 transition-colors">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Image Preview */}
      {imagePreview && (
        <div className="px-4 py-2 border-t bg-slate-50 flex items-center gap-2">
          <img src={imagePreview} alt="" className="h-16 rounded-md" />
          <button onClick={() => { setImageFile(null); setImagePreview(null); }} className="text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSend} className="px-4 py-3 border-t bg-white flex items-center gap-2">
        <div className={`w-3 h-3 rounded-full shrink-0 ${getStoreColor(selectedStore)}`} title={getStoreLabel(selectedStore)} />
        <button type="button" onClick={() => fileInputRef.current?.click()} className="text-slate-400 hover:text-slate-600 shrink-0">
          <ImagePlus className="w-5 h-5" />
        </button>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
        <Input
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder={`Message from ${getStoreLabel(selectedStore)}...`}
          className="flex-1"
          autoFocus
          data-testid="chat-input"
        />
        <Button type="submit" disabled={sending || (!newMessage.trim() && !imageFile)} className="bg-slate-900 hover:bg-slate-800" data-testid="chat-send">
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
}
